import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Search, 
  Download, 
  Check, 
  AlertCircle, 
  Loader2, 
  Languages, 
  Key,
  ExternalLink,
  Subtitles,
  Upload,
  RotateCcw,
  Sparkles
} from 'lucide-react';
import { SubtitleRelease, SubtitleCue, UserProfile } from '../types';
import { getApiBaseUrl } from '../utils/apiConfig';
import { auth, updateUserPreferences, getUserProfile } from '../lib/firebase';

interface SubdlSubtitleModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaTitle: string;
  originalTitle?: string;
  mediaType: 'movie' | 'series';
  tmdbId?: number;
  season?: number;
  episode?: number;
  episodeTitle?: string;
  onSelectSubtitle: (subtitle: { 
    release_name: string; 
    lang: string; 
    cues: SubtitleCue[]; 
    vtt: string;
    filename: string;
  }) => void;
}

const LANGUAGE_FILTERS = [
  { code: 'ALL', label: 'Tots' },
  { code: 'CA', label: 'Català', badge: 'CA', color: 'bg-amber-500/20 text-amber-300 border-amber-500/40' },
  { code: 'ES', label: 'Castellà', badge: 'ES', color: 'bg-red-500/20 text-red-300 border-red-500/40' },
  { code: 'JA', label: 'Japonès', badge: 'JA', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' },
  { code: 'EN', label: 'Anglès', badge: 'EN', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' },
  { code: 'FR', label: 'Francès', badge: 'FR', color: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
];

export function parseClientSrtOrVtt(rawText: string): { vtt: string; cues: SubtitleCue[] } {
  const cleanText = rawText.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  const blocks = cleanText.split(/\n\s*\n/);
  const cues: SubtitleCue[] = [];
  const vttLines: string[] = ['WEBVTT\n'];

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length === 0) continue;

    let timeLineIdx = -1;
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].includes('-->')) {
        timeLineIdx = i;
        break;
      }
    }
    if (timeLineIdx === -1) continue;

    const timeLine = lines[timeLineIdx];
    // Matches: 00:00:00.000 --> 00:00:00.000 or 00:00.000 --> 00:00.000 or with comma
    const match = timeLine.match(/(?:(\d{1,2}):)?(\d{1,2}):(\d{2})[,.](\d{2,3})\s*-->\s*(?:(\d{1,2}):)?(\d{1,2}):(\d{2})[,.](\d{2,3})/);
    if (!match) continue;

    const startH = match[1] ? parseInt(match[1], 10) : 0;
    const startM = parseInt(match[2], 10);
    const startS = parseInt(match[3], 10);
    const startMs = parseInt(match[4].padEnd(3, '0'), 10);
    const startSeconds = startH * 3600 + startM * 60 + startS + startMs / 1000;

    const endH = match[5] ? parseInt(match[5], 10) : 0;
    const endM = parseInt(match[6], 10);
    const endS = parseInt(match[7], 10);
    const endMs = parseInt(match[8].padEnd(3, '0'), 10);
    const endSeconds = endH * 3600 + endM * 60 + endS + endMs / 1000;

    const rawCueText = lines.slice(timeLineIdx + 1).join('\n').trim();
    const cueText = rawCueText.replace(/<[^>]+>/g, '').trim();
    if (!cueText) continue;

    cues.push({ start: startSeconds, end: endSeconds, text: cueText });

    const fmt = (sec: number) => {
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = Math.floor(sec % 60);
      const ms = Math.floor((sec % 1) * 1000);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
    };

    vttLines.push(`${fmt(startSeconds)} --> ${fmt(endSeconds)}\n${cueText}\n`);
  }

  return { vtt: vttLines.join('\n'), cues };
}

export const SubdlSubtitleModal: React.FC<SubdlSubtitleModalProps> = ({
  isOpen,
  onClose,
  mediaTitle,
  originalTitle,
  mediaType,
  tmdbId,
  season = 1,
  episode = 1,
  episodeTitle,
  onSelectSubtitle
}) => {
  const [searchTitle, setSearchTitle] = useState(mediaTitle);
  const [currentTmdbId, setCurrentTmdbId] = useState<string>(tmdbId ? String(tmdbId) : '');
  const [selectedLangFilter, setSelectedLangFilter] = useState<string>('ALL');
  const [subtitles, setSubtitles] = useState<SubtitleRelease[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSubdlKey, setHasSubdlKey] = useState<boolean | null>(null);
  const [customApiKey, setCustomApiKey] = useState<string>(() => {
    try {
      return localStorage.getItem('subdl_user_api_key') || '';
    } catch {
      return '';
    }
  });
  const [inputApiKey, setInputApiKey] = useState('');
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [isKeyConfigOpen, setIsKeyConfigOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state when props change
  useEffect(() => {
    if (isOpen) {
      setSearchTitle(mediaTitle);
      setCurrentTmdbId(tmdbId ? String(tmdbId) : '');
      checkConfigAndSearch();
    }
  }, [isOpen, mediaTitle, originalTitle, tmdbId, season, episode]);

  // Check backend config
  const checkConfigAndSearch = async () => {
    const apiBase = getApiBaseUrl();
    let serverHasKey = false;
    try {
      const configRes = await fetch(`${apiBase}/api/subtitles/config`);
      if (configRes.ok) {
        const config = await configRes.json();
        serverHasKey = Boolean(config.hasSubdlKey);
      }
    } catch {
      // Fallback
    }

    const effectiveHasKey = serverHasKey || Boolean(customApiKey.trim());
    setHasSubdlKey(effectiveHasKey);

    if (effectiveHasKey) {
      executeSearch();
    } else {
      setIsKeyConfigOpen(true);
    }
  };

  const executeSearch = async (forcedTitle?: string, forcedTmdbId?: string) => {
    setIsLoading(true);
    setError(null);

    const apiBase = getApiBaseUrl();
    const queryTitle = (forcedTitle !== undefined ? forcedTitle : searchTitle).trim();
    const queryTmdbId = (forcedTmdbId !== undefined ? forcedTmdbId : currentTmdbId).trim();

    try {
      const params = new URLSearchParams({
        title: queryTitle,
        type: mediaType,
        languages: 'ca,es,ja,en,fr,de,it,pt' // Query all common languages so tabs have accurate counts
      });

      if (originalTitle && originalTitle.trim()) {
        params.set('original_title', originalTitle.trim());
      }

      if (queryTmdbId && queryTmdbId !== '0') {
        params.set('tmdb_id', queryTmdbId);
      }

      if (mediaType === 'series') {
        params.set('season', String(season));
        params.set('episode', String(episode));
      }

      if (customApiKey.trim()) {
        params.set('api_key', customApiKey.trim());
      }

      const res = await fetch(`${apiBase}/api/subtitles/search?${params.toString()}`);
      const data = await res.json();

      if (!res.ok) {
        if (data.requiresKey) {
          setHasSubdlKey(false);
          setIsKeyConfigOpen(true);
        }
        throw new Error(data.error || 'Error en cercar subtítols');
      }

      if (data.tmdbId && !currentTmdbId) {
        setCurrentTmdbId(data.tmdbId);
      }

      setSubtitles(data.subtitles || []);
      if ((!data.subtitles || data.subtitles.length === 0) && data.message) {
        setError(data.message);
      }
    } catch (err: any) {
      setError(err.message || 'Error de connexió en cercar a SubDL');
      setSubtitles([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveApiKey = async () => {
    const trimmed = inputApiKey.trim();
    if (!trimmed) return;
    try {
      localStorage.setItem('subdl_user_api_key', trimmed);
    } catch {
      // Ignore storage errors
    }
    setCustomApiKey(trimmed);
    setHasSubdlKey(true);
    setIsKeyConfigOpen(false);

    // Also persist to current user's profile preferences in Firestore if authenticated
    if (auth.currentUser) {
      try {
        const profile = await getUserProfile(auth.currentUser.uid);
        if (profile) {
          await updateUserPreferences(auth.currentUser.uid, {
            ...(profile.preferences || {}),
            subdlApiKey: trimmed
          });
        }
      } catch (err) {
        console.warn('Could not persist SubDL key to Firestore profile:', err);
      }
    }

    executeSearch();
  };

  const handleRemoveApiKey = async () => {
    try {
      localStorage.removeItem('subdl_user_api_key');
    } catch {
      // Ignore
    }
    setCustomApiKey('');
    setInputApiKey('');
    setHasSubdlKey(false);
    setIsKeyConfigOpen(true);

    if (auth.currentUser) {
      try {
        const profile = await getUserProfile(auth.currentUser.uid);
        if (profile) {
          await updateUserPreferences(auth.currentUser.uid, {
            ...(profile.preferences || {}),
            subdlApiKey: ''
          });
        }
      } catch (err) {
        console.warn('Could not remove SubDL key from Firestore profile:', err);
      }
    }
  };

  const handleDownloadSubtitle = async (sub: SubtitleRelease) => {
    setDownloadingId(String(sub.id));
    setError(null);

    const apiBase = getApiBaseUrl();

    try {
      const res = await fetch(`${apiBase}/api/subtitles/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: sub.url,
          season: mediaType === 'series' ? season : undefined,
          episode: mediaType === 'series' ? episode : undefined,
          api_key: customApiKey.trim() || undefined
        })
      });

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'No s\'ha pogut descarregar el subtítol');
      }

      // Pass parsed cues and VTT back to VideoPlayerModal
      onSelectSubtitle({
        release_name: sub.release_name || sub.name || 'Subtítol SubDL',
        lang: sub.lang || sub.language,
        cues: data.cues || [],
        vtt: data.vtt || '',
        filename: data.filename || 'subtitles.vtt'
      });

      onClose();
    } catch (err: any) {
      setError(`Error en descarregar: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (!content) return;

      const { vtt, cues } = parseClientSrtOrVtt(content);
      if (cues.length === 0) {
        setError("El fitxer seleccionat no sembla contenir cap línia de subtítols vàlida.");
        return;
      }

      onSelectSubtitle({
        release_name: file.name,
        lang: 'Local',
        cues,
        vtt,
        filename: file.name
      });

      onClose();
    };
    reader.onerror = () => {
      setError("Error en llegir el fitxer local.");
    };
    reader.readAsText(file);
  };

  if (!isOpen) return null;

  // Filter subtitles by active tab
  const displayedSubtitles = subtitles.filter(sub => {
    if (selectedLangFilter === 'ALL') return true;
    const subLang = (sub.language || sub.lang || '').toLowerCase();
    const filterCode = selectedLangFilter.toLowerCase();
    if (filterCode === 'ca') return subLang.includes('ca') || subLang.includes('catalan') || subLang.includes('valencian');
    if (filterCode === 'es') return subLang.includes('es') || subLang.includes('spanish') || subLang.includes('castellano');
    if (filterCode === 'ja') return subLang.includes('ja') || subLang.includes('jpn') || subLang.includes('japanese') || subLang.includes('jap');
    if (filterCode === 'fr') return subLang.includes('fr') || subLang.includes('french');
    if (filterCode === 'en') return subLang.includes('en') || subLang.includes('english');
    return true;
  });

  const countByLang = (code: string) => {
    if (code === 'ALL') return subtitles.length;
    const filterCode = code.toLowerCase();
    return subtitles.filter(sub => {
      const subLang = (sub.language || sub.lang || '').toLowerCase();
      if (filterCode === 'ca') return subLang.includes('ca') || subLang.includes('catalan') || subLang.includes('valencian');
      if (filterCode === 'es') return subLang.includes('es') || subLang.includes('spanish') || subLang.includes('castellano');
      if (filterCode === 'ja') return subLang.includes('ja') || subLang.includes('jpn') || subLang.includes('japanese') || subLang.includes('jap');
      if (filterCode === 'fr') return subLang.includes('fr') || subLang.includes('french');
      if (filterCode === 'en') return subLang.includes('en') || subLang.includes('english');
      return false;
    }).length;
  };

  const getLanguageBadge = (sub: SubtitleRelease) => {
    const lang = (sub.language || sub.lang || '').toLowerCase();
    if (lang.includes('ca') || lang.includes('catalan') || lang.includes('valencian')) {
      return { code: 'CA', label: 'Català', color: 'bg-amber-500/20 text-amber-300 border-amber-500/40' };
    }
    if (lang.includes('es') || lang.includes('spanish') || lang.includes('castellano')) {
      return { code: 'ES', label: 'Castellà', color: 'bg-red-500/20 text-red-300 border-red-500/40' };
    }
    if (lang.includes('ja') || lang.includes('jpn') || lang.includes('japanese') || lang.includes('jap')) {
      return { code: 'JA', label: 'Japonès', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' };
    }
    if (lang.includes('fr') || lang.includes('french')) {
      return { code: 'FR', label: 'Francès', color: 'bg-blue-500/20 text-blue-300 border-blue-500/40' };
    }
    if (lang.includes('en') || lang.includes('english')) {
      return { code: 'EN', label: 'Anglès', color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' };
    }
    if (lang.includes('de') || lang.includes('german') || lang.includes('deu')) {
      return { code: 'DE', label: 'Alemany', color: 'bg-orange-500/20 text-orange-300 border-orange-500/40' };
    }
    if (lang.includes('it') || lang.includes('italian') || lang.includes('ita')) {
      return { code: 'IT', label: 'Italià', color: 'bg-green-500/20 text-green-300 border-green-500/40' };
    }
    if (lang.includes('pt') || lang.includes('portuguese') || lang.includes('por')) {
      return { code: 'PT', label: 'Portuguès', color: 'bg-teal-500/20 text-teal-300 border-teal-500/40' };
    }
    return { code: (sub.lang || 'SUB').toUpperCase().slice(0, 2), label: sub.lang || 'Sub', color: 'bg-slate-500/20 text-slate-300 border-slate-500/40' };
  };

  return (
    <div 
      id="subdl-subtitle-modal-backdrop"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-in fade-in"
      onClick={onClose}
    >
      <div 
        id="subdl-subtitle-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[92vh] bg-slate-900 border border-white/15 rounded-2xl shadow-2xl flex flex-col overflow-hidden text-white animate-in zoom-in-95 duration-200"
      >
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-white/10 flex items-center justify-between shrink-0 bg-slate-900/90">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-sky-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400">
              <Subtitles className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-display font-bold text-base sm:text-lg text-white">
                  Cercador de Subtítols (SubDL)
                </h3>
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500 text-black uppercase tracking-wider">
                  API
                </span>
              </div>
              <p className="text-xs text-slate-400 truncate max-w-sm sm:max-w-md">
                {mediaTitle} {mediaType === 'series' && `• T${season} E${episode}${episodeTitle ? `: ${episodeTitle}` : ''}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Hidden file input for local subtitles */}
            <input 
              type="file" 
              ref={fileInputRef} 
              accept=".srt,.vtt" 
              className="hidden" 
              onChange={handleFileUpload} 
            />
            <button
              type="button"
              data-focusable="true"
              onClick={() => fileInputRef.current?.click()}
              title="Pujar fitxer .srt / .vtt des del teu dispositiu"
              className="px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white text-xs flex items-center gap-1.5 transition-colors"
            >
              <Upload className="w-3.5 h-3.5 text-amber-400" />
              <span className="hidden sm:inline">Pujar arxiu</span>
            </button>
            <button
              id="btn-close-subdl-modal"
              type="button"
              data-focusable="true"
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* SubDL API Key Configuration Box */}
        {(hasSubdlKey === false || isKeyConfigOpen) && (
          <div className="p-4 mx-4 mt-3 rounded-xl bg-gradient-to-br from-amber-500/10 via-slate-900 to-black border border-amber-500/30 text-xs space-y-3 shrink-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <Key className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="font-semibold text-amber-200">
                  {customApiKey ? "Clau d'API de SubDL activa" : "Configura la teva clau d'API de SubDL"}
                </span>
              </div>
              <a
                href="https://subdl.com/panel/api"
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-sky-400 hover:text-sky-300 flex items-center gap-1 hover:underline"
              >
                <span>Aconseguir clau gratuïta</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <p className="text-slate-300 text-[11px] leading-relaxed">
              SubDL requereix una clau d'API per cercar subtítols. Pots obtenir una clau 100% gratuïta (2.000 cerques/dia) en 30 segons creant un compte a <span className="text-white font-medium">subdl.com</span>.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-2">
              <input
                type="text"
                value={inputApiKey}
                onChange={(e) => setInputApiKey(e.target.value)}
                placeholder={customApiKey ? `Clau actual: ${customApiKey.slice(0, 6)}...` : "Enganxa aquí la teva clau d'API de SubDL..."}
                className="w-full px-3 py-2 rounded-lg bg-black/60 border border-white/15 text-white placeholder-slate-500 text-xs font-mono focus:outline-none focus:border-amber-400"
              />
              <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
                <button
                  type="button"
                  data-focusable="true"
                  onClick={handleSaveApiKey}
                  disabled={!inputApiKey.trim()}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs flex items-center justify-center gap-1.5 transition-colors disabled:opacity-40"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Desar i cercar</span>
                </button>
                {customApiKey && (
                  <button
                    type="button"
                    data-focusable="true"
                    onClick={handleRemoveApiKey}
                    className="px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs transition-colors"
                  >
                    Eliminar
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Language Filter Tabs & Tools */}
        <div className="px-4 pt-3 pb-2 flex flex-wrap items-center justify-between gap-2 border-b border-white/5 shrink-0">
          <div className="flex items-center gap-1.5 overflow-x-auto py-1">
            {LANGUAGE_FILTERS.map(filter => {
              const count = countByLang(filter.code);
              return (
                <button
                  key={filter.code}
                  id={`subdl-lang-filter-${filter.code}`}
                  type="button"
                  data-focusable="true"
                  onClick={() => setSelectedLangFilter(filter.code)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                    selectedLangFilter === filter.code
                      ? 'bg-amber-500 text-black shadow-lg shadow-amber-500/20 font-bold'
                      : 'bg-white/5 hover:bg-white/10 text-slate-300'
                  }`}
                >
                  <span>{filter.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded font-mono ${
                    selectedLangFilter === filter.code 
                      ? 'bg-black/20 text-black' 
                      : count > 0 ? 'bg-amber-500/20 text-amber-300' : 'bg-white/10 text-slate-400'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              data-focusable="true"
              onClick={() => setIsKeyConfigOpen(!isKeyConfigOpen)}
              title="Gestionar clau d'API de SubDL"
              className={`p-1.5 rounded-lg border text-xs flex items-center gap-1 transition-colors ${
                customApiKey 
                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20' 
                  : 'bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20'
              }`}
            >
              <Key className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{customApiKey ? 'Clau API' : 'Afegir clau'}</span>
            </button>

            <button
              type="button"
              data-focusable="true"
              onClick={() => setIsAdvancedSearchOpen(!isAdvancedSearchOpen)}
              className="text-xs text-sky-400 hover:text-sky-300 underline underline-offset-4 flex items-center gap-1"
            >
              <Search className="w-3.5 h-3.5" />
              <span>{isAdvancedSearchOpen ? 'Amagar filtres' : 'Ajustar cerca'}</span>
            </button>
          </div>
        </div>

        {/* Optional Search Refinement Form */}
        {isAdvancedSearchOpen && (
          <div className="p-3 mx-4 my-2 rounded-xl bg-white/5 border border-white/10 space-y-2 text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-slate-400 block mb-1">Títol a cercar</label>
                <input
                  type="text"
                  value={searchTitle}
                  onChange={(e) => setSearchTitle(e.target.value)}
                  className="w-full px-3 py-1.5 rounded-lg bg-black/50 border border-white/10 text-white text-xs focus:outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="text-slate-400 block mb-1">TMDB ID (opcional)</label>
                <input
                  type="text"
                  value={currentTmdbId}
                  onChange={(e) => setCurrentTmdbId(e.target.value)}
                  placeholder="Ex: 12609"
                  className="w-full px-3 py-1.5 rounded-lg bg-black/50 border border-white/10 text-white text-xs focus:outline-none focus:border-amber-400"
                />
              </div>
            </div>

            {originalTitle && originalTitle !== searchTitle && (
              <div className="flex items-center justify-between text-[11px] pt-1 text-slate-400">
                <span>Títol original disponible: <strong className="text-slate-200">{originalTitle}</strong></span>
                <button
                  type="button"
                  data-focusable="true"
                  onClick={() => {
                    setSearchTitle(originalTitle);
                    executeSearch(originalTitle);
                  }}
                  className="text-amber-400 hover:text-amber-300 underline font-medium"
                >
                  Cercar pel títol original
                </button>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <button
                type="button"
                data-focusable="true"
                onClick={() => executeSearch()}
                disabled={isLoading}
                className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs flex items-center gap-1.5 transition-colors disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                <span>Tornar a cercar</span>
              </button>
            </div>
          </div>
        )}

        {/* Results List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5 min-h-[240px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-14 text-slate-400 gap-3">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
              <p className="text-xs">Cercant subtítols a SubDL...</p>
            </div>
          ) : error && displayedSubtitles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center px-4">
              <AlertCircle className="w-8 h-8 text-amber-400 mb-2 opacity-80" />
              <p className="text-xs font-semibold text-slate-300 max-w-md">{error}</p>
              
              <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
                {originalTitle && originalTitle !== searchTitle && (
                  <button
                    type="button"
                    data-focusable="true"
                    onClick={() => {
                      setSearchTitle(originalTitle);
                      executeSearch(originalTitle);
                    }}
                    className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 text-xs font-semibold flex items-center gap-1.5 transition-colors"
                  >
                    <Search className="w-3.5 h-3.5" />
                    <span>Provar títol original ({originalTitle})</span>
                  </button>
                )}

                <button
                  type="button"
                  data-focusable="true"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors"
                >
                  <Upload className="w-3.5 h-3.5 text-amber-400" />
                  <span>Carregar fitxer .srt / .vtt local</span>
                </button>
              </div>
            </div>
          ) : displayedSubtitles.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500 gap-3 text-center px-4">
              <Subtitles className="w-8 h-8 opacity-40" />
              <p className="text-xs">No hi ha subtítols per al filtre seleccionat ({selectedLangFilter}).</p>
              
              <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
                {selectedLangFilter !== 'ALL' && subtitles.length > 0 && (
                  <button
                    type="button"
                    data-focusable="true"
                    onClick={() => setSelectedLangFilter('ALL')}
                    className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-slate-200 text-xs transition-colors"
                  >
                    Veure tots els idiomes ({subtitles.length})
                  </button>
                )}

                {originalTitle && originalTitle !== searchTitle && (
                  <button
                    type="button"
                    data-focusable="true"
                    onClick={() => {
                      setSearchTitle(originalTitle);
                      executeSearch(originalTitle);
                    }}
                    className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-xs transition-colors"
                  >
                    Provar cerca: "{originalTitle}"
                  </button>
                )}

                <button
                  type="button"
                  data-focusable="true"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-slate-200 text-xs flex items-center gap-1.5 transition-colors"
                >
                  <Upload className="w-3.5 h-3.5 text-amber-400" />
                  <span>Carregar fitxer manual</span>
                </button>
              </div>
            </div>
          ) : (
            displayedSubtitles.map((sub, idx) => {
              const badge = getLanguageBadge(sub);
              const isDownloading = downloadingId === String(sub.id);

              return (
                <div
                  key={sub.id || idx}
                  className="p-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/15 flex items-center justify-between gap-3 transition-all"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-black tracking-wider border ${badge.color}`}>
                        {badge.code} • {badge.label}
                      </span>
                      {sub.hi && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                          HI (Audició)
                        </span>
                      )}
                      {sub.author && (
                        <span className="text-[11px] text-slate-400">
                          per <span className="text-slate-300 font-medium">{sub.author}</span>
                        </span>
                      )}
                    </div>
                    <p className="text-xs font-semibold text-slate-100 truncate" title={sub.release_name}>
                      {sub.release_name}
                    </p>
                    {sub.comment && (
                      <p className="text-[11px] text-slate-400 italic line-clamp-1">
                        "{sub.comment}"
                      </p>
                    )}
                  </div>

                  <button
                    id={`btn-apply-subdl-${idx}`}
                    type="button"
                    data-focusable="true"
                    disabled={isDownloading}
                    onClick={() => handleDownloadSubtitle(sub)}
                    className="px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-black font-bold text-xs flex items-center gap-1.5 transition-all shadow-md shadow-amber-500/20 shrink-0 disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Descarregant...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-3.5 h-3.5" />
                        <span>Aplicar</span>
                      </>
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-4 border-t border-white/10 bg-slate-900/90 flex items-center justify-between text-xs text-slate-400 shrink-0">
          <div className="flex items-center gap-2">
            <span>{displayedSubtitles.length} versions trobades</span>
            {customApiKey && (
              <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-emerald-400">
                <Check className="w-3 h-3" />
                <span>Clau SubDL activa</span>
              </span>
            )}
          </div>
          <button
            type="button"
            data-focusable="true"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-slate-200 text-xs font-semibold transition-colors"
          >
            Tancar
          </button>
        </div>
      </div>
    </div>
  );
};
